// The ways to change a person, shared by the People list and a person's own page, so
// both offer the same actions with the same wording.
import { useState } from 'react';
import { api } from '../../api.js';
import Field from '../../components/Field.jsx';
import Alert from '../../components/ui/Alert.jsx';
import ConfirmModal from '../../components/ui/ConfirmModal.jsx';
import Modal, { ModalActions } from '../../components/ui/Modal.jsx';
import { KindField, ScopeField, coversText, scopeFromKey, scopeKey } from './peopleShared.jsx';
import { checkDateOrder, checkRequired } from '../../utils/validate.js';

export function EditPersonModal({ base, person, onClose, onSaved }) {
  const [form, setForm] = useState({ first_name: person.first_name, last_name: person.last_name, job_title: person.job_title || '' });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k, v) => { setForm({ ...form, [k]: v }); if (errors[k]) setErrors({ ...errors, [k]: undefined }); };
  const save = async () => {
    const found = Object.fromEntries(Object.entries({
      first_name: checkRequired(form.first_name, 'their first name', { max: 100 }),
      last_name: checkRequired(form.last_name, 'their last name', { max: 100 }),
    }).filter(([, m]) => m));
    if (Object.keys(found).length) { setErrors(found); return; }
    setBusy(true); setError('');
    try { await api(base, { method: 'PATCH', body: form }); onSaved(); } catch (err) {
      setErrors(err.data?.fields || {}); setError(err.data?.fields ? '' : err.message); setBusy(false);
    }
  };
  return (
    <Modal title="Edit details" onClose={onClose} onSubmit={save} busy={busy}
      footer={<ModalActions submitLabel="Save" busyLabel="Saving…" busy={busy} onCancel={onClose} />}>
      {error && <Alert tone="danger">{error}</Alert>}
      <div className="row-2">
        <Field label="First name" required maxLength={100} value={form.first_name} onChange={(e) => set('first_name', e.target.value)} error={errors.first_name} />
        <Field label="Last name" required maxLength={100} value={form.last_name} onChange={(e) => set('last_name', e.target.value)} error={errors.last_name} />
      </div>
      <Field label="Job title" optional maxLength={100} value={form.job_title} onChange={(e) => set('job_title', e.target.value)} error={errors.job_title} />
    </Modal>
  );
}

/** Add what they cover, or (with `cover`) change an existing role: new job, other events, new dates. */
export function CoverModal({ base, data, cover, onClose, onSaved }) {
  const [form, setForm] = useState(cover
    ? { kind: cover.kind, role_label: cover.role_label || '', scope: scopeKey(cover.covers), valid_from: cover.valid_from || '', valid_until: cover.valid_until || '' }
    : { kind: 'employee', role_label: '', scope: 'company', valid_from: '', valid_until: '' });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k, v) => { setForm({ ...form, [k]: v }); if (errors[k]) setErrors({ ...errors, [k]: undefined }); };
  const save = async () => {
    const order = checkDateOrder(form.valid_from, form.valid_until);
    if (order) { setErrors({ valid_until: order }); return; }
    setBusy(true); setError('');
    try {
      const res = await api(cover ? `${base}/relationships/${cover.id}` : `${base}/relationships`,
        { method: cover ? 'PATCH' : 'POST', body: { ...form, scope: scopeFromKey(form.scope) } });
      onSaved(res.covers);
    } catch (err) { setErrors(err.data?.fields || {}); setError(err.data?.fields ? '' : err.message); setBusy(false); }
  };
  return (
    <Modal title={cover ? 'Change their role' : 'Add what they cover'} onClose={onClose} onSubmit={save} busy={busy}
      description={cover
        ? 'For a new job title, other events or new dates. Their addresses count for what you choose here from the moment you save.'
        : 'Their addresses count for these events, between these dates.'}
      footer={<ModalActions submitLabel={cover ? 'Save' : 'Add'} busyLabel={cover ? 'Saving…' : 'Adding…'} busy={busy} onCancel={onClose} />}>
      {error && <Alert tone="danger">{error}</Alert>}
      <ScopeField value={form.scope} onChange={(v) => set('scope', v)} scopes={data.scopes} companyName={data.organisation.name} error={errors.scope} />
      <KindField value={form.kind} onChange={(v) => set('kind', v)} kinds={data.kinds} error={errors.kind} />
      <Field label="Role" optional maxLength={200} value={form.role_label} onChange={(e) => set('role_label', e.target.value)} placeholder="Exhibitor sales" error={errors.role_label} />
      <div className="row-2">
        <Field label="From" optional type="date" value={form.valid_from} onChange={(e) => set('valid_from', e.target.value)} error={errors.valid_from} />
        <Field label="Until" optional type="date" min={form.valid_from || undefined} value={form.valid_until} onChange={(e) => set('valid_until', e.target.value)} error={errors.valid_until}
          hint={!errors.valid_until ? (cover ? 'If they’ve moved on from this role, set the last day here.' : 'Leave both empty if it has no end.') : undefined} />
      </div>
    </Modal>
  );
}

export function LeftModal({ base, person, onClose, onSaved }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api(`${base}/left`, { method: 'POST', body: { left_on: date } }); onSaved(); } catch (err) { setError(err.data?.fields?.left_on || err.message); setBusy(false); }
  };
  return (
    <Modal title={`${person.first_name} has left?`} onClose={onClose} onSubmit={save} busy={busy} size="sm"
      footer={<ModalActions submitLabel="Save" busyLabel="Saving…" busy={busy} onCancel={onClose} />}>
      <p className="modal-text">From this date their addresses and numbers stop counting as yours. Anything sent from them afterwards cannot be verified, and you&rsquo;ll be told if it&rsquo;s reported.</p>
      <Field label="Last day" type="date" value={date} onChange={(e) => setDate(e.target.value)} error={error} />
    </Modal>
  );
}

/** "They're back": their confirmed addresses count again. */
export function BackModal({ base, person, onClose, onSaved }) {
  return (
    <ConfirmModal title={`${person.first_name} is back?`} confirmLabel="Yes, they’re back" onClose={onClose}
      onConfirm={async () => { await api(`${base}/left`, { method: 'POST', body: { left_on: null } }); await onSaved(); }}>
      <p>Their confirmed addresses and numbers count as yours again, for what they cover.</p>
    </ConfirmModal>
  );
}

/** Stop one role counting. */
export function UncoverModal({ base, cover, onClose, onSaved }) {
  return (
    <ConfirmModal title={`Stop counting them for ${coversText(cover)}?`} confirmLabel="Stop counting" danger onClose={onClose}
      onConfirm={async () => { await api(`${base}/relationships/${cover.id}`, { method: 'DELETE' }); await onSaved(); }}>
      <p>Their addresses stop counting for <strong>{coversText(cover)}</strong>. If they&rsquo;ve changed role, use &ldquo;Change&rdquo; instead.</p>
    </ConfirmModal>
  );
}

/** Delete them for good. Points at "They've left", which is usually what's meant. */
export function RemovePersonModal({ base, person, onClose, onRemoved }) {
  return (
    <ConfirmModal title={`Remove ${person.name}?`} confirmLabel="Remove for good" danger typeToConfirm={person.name} onClose={onClose}
      onConfirm={async () => { await api(base, { method: 'DELETE' }); await onRemoved(); }}>
      <p>This deletes {person.first_name} and their addresses and numbers for good: they stop counting as yours straight away, for every event, and can&rsquo;t be brought back.</p>
      <p>If they&rsquo;ve simply left, use &ldquo;They&rsquo;ve left&rdquo; instead: that keeps the record, so anything they send afterwards is flagged.</p>
    </ConfirmModal>
  );
}
